// recentlyViewedItems.tsx
// Implements a plugin for Algolia Autocomplete to show recently viewed products.
// Manages local storage and UI for displaying recently viewed search items.

import { AutocompletePlugin, AutocompleteSource, AutocompleteState } from '@algolia/autocomplete-js';
import {
  createLocalStorageRecentSearchesPlugin,
  search,
  SearchParams,
} from '@algolia/autocomplete-plugin-recent-searches';
import { Highlighted } from '@algolia/autocomplete-plugin-recent-searches/dist/esm/types';
import { MaybePromise } from '@algolia/autocomplete-shared';
import Link from 'next/link';
import { Fragment } from 'react';

type RecentlyViewedRecord = {
  id: string;
  label: string;
  url: string;
  image: string;
};

type CreateLocalStorageRecentlyViewedItemsParams<TItem extends RecentlyViewedRecord> = {
  key: string;
  limit?: number;
  search?(params: SearchParams<TItem>): Array<Highlighted<TItem>>;
  transformSource?(params: {
    source: AutocompleteSource<TItem>;
    state: AutocompleteState<TItem>;
    onRemove(id: string): void;
  }): AutocompleteSource<TItem>;
};

type RecentlyViewedItemsPluginData<TItem extends RecentlyViewedRecord> = {
  addItem(item: TItem): void;
  removeItem(id: string): void;
  getAll(query?: string): MaybePromise<Array<Highlighted<TItem>>>;
};

export function createLocalStorageRecentlyViewedItems<TItem extends RecentlyViewedRecord>(
  params: CreateLocalStorageRecentlyViewedItemsParams<TItem>,
): AutocompletePlugin<TItem, RecentlyViewedItemsPluginData<TItem>> {
  const { onReset, onSubmit, subscribe, ...plugin } = createLocalStorageRecentSearchesPlugin<TItem>({
    ...params,
    search(params) {
      if (params?.query) {
        return [];
      }

      return search(params);
    },
    transformSource({ source, onRemove, state }) {
      const transformedSource = params?.transformSource ? params?.transformSource({ source, onRemove, state }) : source;

      // Compose items: recently viewed, divider, trending
      const originalGetItems = transformedSource?.getItems;
      // Provide onRemove to the item template via closure
      const itemTemplate = (itemProps: { item: any; components: any }) => {
        const { item, components } = itemProps;
        if ((item as any)?.__divider) {
          return (
            <div className="my-4 flex w-full cursor-default items-center">
              <span className="whitespace-nowrap text-[0.8em] font-semibold text-[#3E34D3]">Trending Search</span>
              <div className="ml-4 h-px flex-1 bg-[#3E34D3] opacity-[.3]" />
            </div>
          );
        }
        return (
          <div className="aa-ItemContent grid w-full grid-cols-[1fr_auto] items-center gap-4">
            <Link className="aa-ItemLink" href={item?.url}>
              <div className="flex w-full items-center">
                {item?.image ? (
                  <div className="aa-ItemIcon">
                    <img src={item?.image} alt={item?.label} />
                  </div>
                ) : (
                  <div className="aa-ItemIcon aa-ItemIcon--noBorder">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.516 6.984v5.25l4.5 2.672-0.75 1.266-5.25-3.188v-6h1.5zM12 20.016q3.281 0 5.648-2.367t2.367-5.648-2.367-5.648-5.648-2.367-5.648 2.367-2.367 5.648 2.367 5.648 5.648 2.367zM12 2.016q4.125 0 7.055 2.93t2.93 7.055-2.93 7.055-7.055 2.93-7.055-2.93-2.93-7.055 2.93-7.055 7.055-2.93z" />
                    </svg>
                  </div>
                )}

                <div className="aa-ItemContentBody ml-3 flex flex-1 items-center">
                  <div className="aa-ItemContentTitle truncate text-base font-medium">
                    <components.Highlight hit={item} attribute="label" />
                  </div>
                </div>
              </div>
            </Link>

            <div className="justify-self-end">
              <button
                type="button"
                className="ml-3 flex flex-shrink-0 cursor-pointer items-center border-none bg-transparent"
                title="Remove this search"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove(item?.id);
                }}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="#e53935">
                  <path d="M18 7v13c0 0.276-0.111 0.525-0.293 0.707s-0.431 0.293-0.707 0.293h-10c-0.276 0-0.525-0.111-0.707-0.293s-0.293-0.431-0.293-0.707v-13zM17 5v-1c0-0.828-0.337-1.58-0.879-2.121s-1.293-0.879-2.121-0.879h-4c-0.828 0-1.58 0.337-2.121 0.879s-0.879 1.293-0.879 2.121v1h-4c-0.552 0-1 0.448-1 1s0.448 1 1 1h1v13c0 0.828 0.337 1.58 0.879 2.121s1.293 0.879 2.121 0.879h10c0.828 0 1.58-0.337 2.121-0.879s0.879-1.293 0.879-2.121v-13h1c0.552 0 1-0.448 1-1s-0.448-1-1-1zM9 5v-1c0-0.276 0.111-0.525 0.293-0.707s0.431-0.293 0.707-0.293h4c0.276 0 0.525 0.111 0.707 0.293s0.293 0.431 0.293 0.707v1zM9 11v6c0 0.552 0.448 1 1 1s1-0.448 1-1v-6c0-0.552-0.448-1-1-1s-1 0.448-1 1zM13 11v6c0 0.552 0.448 1 1 1s1-0.448 1-1v-6c0-0.552-0.448-1-1-1s-1 0.448-1 1z" />
                </svg>
              </button>
            </div>
          </div>
        );
      };

      return {
        ...transformedSource,
        sourceId: 'recentlyViewedItemsPlugin',
        getItemUrl({ item }) {
          return item?.url;
        },
        getItems(params) {
          const items = originalGetItems(params);
          const hasTrending = true;
          if (Array.isArray(items) && !params?.state?.query && items?.length > 0 && hasTrending) {
            return [...items, { __divider: true } as any];
          }
          return items;
        },
        templates: {
          ...transformedSource?.templates,
          header({ items = [] }) {
            return (
              <Fragment>
                <div className="my-4 flex w-full items-center justify-between">
                  <div className="flex flex-1 items-center">
                    <span className=" whitespace-nowrap text-[0.8em] font-semibold text-[#3E34D3]">
                      Recently Viewed
                    </span>
                    <div className="mx-4 h-px flex-1 bg-[#3E34D3] opacity-[.3]" />
                  </div>

                  {Array.isArray(items) && items?.length > 0 && (
                    <button
                      className="ml-4 cursor-pointer border-none bg-transparent text-[.8em] text-gray-500 hover:text-[#E53935]"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        items?.forEach((item) => onRemove(item?.id));
                      }}
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </Fragment>
            );
          },
          item: itemTemplate,
        },
      };
    },
  });
  const data = plugin.data;

  return {
    ...plugin,
    name: 'aa.localStorageRecentlyViewedItemsPlugin',
    data,
  };
}
